/**
 * ListingLocation Component
 *
 * Displays the listing location: a 300 px, rounded, single-pin map.
 *
 * `load="tap"` (D13): the section sits below the fold, and a frozen pin map
 * that nobody interacts with is a picture — so the page renders the
 * request-free placeholder and the reader's "Show map" tap opens the live,
 * interactive map in place. Before this, every hydrated detail view paid a
 * Dynamic Maps load (22 `maps.googleapis.com` requests at `scrollY = 0`).
 */
import { LocationMap } from '@/shared/components/map/LocationMap';
import { useTranslations } from '@/shared/i18n';
// Rust SSOT ((0,0) Gulf-of-Guinea sentinel, NaN, out-of-range → invalid).
import { isValidCoordinates } from '@letsgethome/lib/wasm';
import { plaintextString } from '@letsgethome/types/branded';

type Props = {
  /** Latitude of the estate */
  latitude: number;
  /** Longitude of the estate */
  longitude: number;
  /** The listing's address — what the section shows if the map cannot load. */
  address?: string;
  /** The listing's district. */
  district?: string;
};

export function ListingLocation({ latitude, longitude, address, district }: Props) {
  const t = useTranslations('listings.detail');

  // Don't render if coordinates are invalid
  if (!isValidCoordinates(latitude, longitude)) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('location')}</h3>
      <LocationMap
        lat={latitude}
        lng={longitude}
        label={t('location')}
        load="tap"
        place={{
          address: address ? plaintextString(address) : undefined,
          district: district ? plaintextString(district) : undefined,
        }}
      />
    </div>
  );
}
